import React, { useEffect, useState } from 'react'
import { fetch_all_metrics, save_metrics_ranges, fetch_metrics_ranges } from '../../../services/medengage/Service';
import { RPM_ALERT_PRIORITY, RPM_ALERT_RANGES } from '../../../constants';

const HealthMetricsSettings = () => {

    const [metrics, setMetrics] = useState([]);
    const [selectedMetric, setSelectedMetric] = useState('');
    const [metricRanges, setMetricRanges] = useState({});
    const [currentRanges, setCurrentRanges] = useState([]);
    const [error, setError] = useState('');
    const [isBloodPressure, setIsBloodPressure] = useState(false);
    const [bpRanges, setBpRanges] = useState({
        systolic: [],
        diastolic: []
    });
    const [activeBpTab, setActiveBpTab] = useState('systolic');

    // New empty range template with additional fields
    const emptyRange = {
        min: '',
        max: '',
        observation: '',
        alertPriority: 0,
        color: '#000000'
    };

    // Define metric limits
    const metricLimits = {
        // Blood Glucose Levels - Fasting (mg/dL)
        1: { min: 0, max: 1000 },
        // Blood Glucose Levels - Postprandial (mg/dL)
        2: { min: 0, max: 1000 },
        // Blood Pressure (mmHg) - handled separately
        6: { min: 0, max: 300 },
        // Pulse Rate (bpm)
        7: { min: 0, max: 300 },
        // Body Temperature (°F)
        8: { min: 80, max: 110 },
        // BMI (kg/m2)
        9: { min: 0, max: 100 },
        // SpO2 (%)
        10: { min: 0, max: 100 }
    };

    // Add new state variables for edit mode and drag handling
    const [editMode, setEditMode] = useState(false);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);

    useEffect(() => {
        fetchAllMetrics();
    }, []);

    // When metric selection changes, load its ranges
    useEffect(() => {
        if (selectedMetric) {
            // Fetch ranges for the selected metric
            fetchMetricRanges(selectedMetric);

            // Check if it's blood pressure
            const isBloodPressureMetric = selectedMetric === '6';
            setIsBloodPressure(isBloodPressureMetric);

            // Reset edit mode when changing metrics
            setEditMode(false);
            setError('');
        } else {
            setCurrentRanges([]);
            setBpRanges({ systolic: [], diastolic: [] });
            setIsBloodPressure(false);
        }
    }, [selectedMetric]);

    // fetch all metrics 
    async function fetchAllMetrics() {
        try {
            const response = await fetch_all_metrics();

            if (response.data.code === 200) {
                console.log(response?.data?.data?.metrics);
                setMetrics(response?.data?.data?.metrics);
            }

        } catch (error) {
            console.log(error);
        }
    }

    // Helper functions to convert between alert priority text and numeric values
    const getAlertPriorityLabel = (value) => {
        return Object.keys(RPM_ALERT_PRIORITY).find(
            key => RPM_ALERT_PRIORITY[key] === parseInt(value)
        ) || 'NORMAL';
    };

    const getAlertPriorityValue = (label) => {
        return RPM_ALERT_PRIORITY[label] || 0;
    };

    // fetch ranges for a specific metric
    async function fetchMetricRanges(metricId) {
        try {
            const response = await fetch_metrics_ranges({ metricsId: metricId });
            if (response.data.code === 200) {
                let responseData = JSON.parse(response?.data?.data?.observations);
                console.log('Metric ranges:', responseData);

                // Update state with fetched data
                if (metricId === '6') {
                    // Handle blood pressure data
                    const bpData = responseData || { systolic: [], diastolic: [] };

                    // Convert numeric alert priorities to labels
                    const processedBpData = {
                        systolic: bpData.systolic?.length ?
                            bpData.systolic.map(range => ({
                                ...range,
                                alertPriorityLabel: getAlertPriorityLabel(range.alertPriority)
                            })) :
                            [{ ...emptyRange, alertPriorityLabel: 'NORMAL' }],
                        diastolic: bpData.diastolic?.length ?
                            bpData.diastolic.map(range => ({
                                ...range,
                                alertPriorityLabel: getAlertPriorityLabel(range.alertPriority)
                            })) :
                            [{ ...emptyRange, alertPriorityLabel: 'NORMAL' }]
                    };

                    setBpRanges(processedBpData);
                } else {
                    // Handle other metrics
                    const ranges = responseData || [];

                    // Convert numeric alert priorities to labels
                    const processedRanges = ranges.length ?
                        ranges.map(range => ({
                            ...range,
                            alertPriorityLabel: getAlertPriorityLabel(range.alertPriority)
                        })) :
                        [{ ...emptyRange, alertPriorityLabel: 'NORMAL' }];

                    setCurrentRanges(processedRanges);
                }

                // Update metric ranges in the main state
                setMetricRanges(prevRanges => ({
                    ...prevRanges,
                    [metricId]: responseData || (metricId === '6' ? { systolic: [], diastolic: [] } : [])
                }));
            }
        } catch (error) {
            console.log(error);
        }
    }

    const handleAddRange = () => {
        if (isBloodPressure) {
            setBpRanges({
                ...bpRanges,
                [activeBpTab]: [...bpRanges[activeBpTab], { ...emptyRange }]
            });
        } else {
            setCurrentRanges([...currentRanges, { ...emptyRange }]);
        }
    };

    const handleRangeChange = (index, field, value) => {
        // Validate inputs for numeric fields
        if ((field === 'min' || field === 'max') && value !== '') {
            // Allow empty string (for infinity), numbers, decimal point, and minus sign at beginning
            if (!/^-?\d*\.?\d*$/.test(value)) {
                return; // Reject non-numeric input
            }
        }

        if (isBloodPressure) {
            const updatedRanges = [...bpRanges[activeBpTab]];

            if (field === 'alertPriorityLabel') {
                // Update both label and numeric value
                updatedRanges[index][field] = value;
                updatedRanges[index]['alertPriority'] = getAlertPriorityValue(value);
            } else {
                updatedRanges[index][field] = value;
            }

            setBpRanges({
                ...bpRanges,
                [activeBpTab]: updatedRanges
            });
        } else {
            const updatedRanges = [...currentRanges];

            if (field === 'alertPriorityLabel') {
                // Update both label and numeric value
                updatedRanges[index][field] = value;
                updatedRanges[index]['alertPriority'] = getAlertPriorityValue(value);
            } else {
                updatedRanges[index][field] = value;
            }

            setCurrentRanges(updatedRanges);
        }
    };

    const handleRemoveRange = (index) => {
        if (isBloodPressure) {
            const updatedRanges = bpRanges[activeBpTab].filter((_, i) => i !== index);
            setBpRanges({
                ...bpRanges,
                [activeBpTab]: updatedRanges.length ? updatedRanges : [{ ...emptyRange }]
            });
        } else {
            const updatedRanges = currentRanges.filter((_, i) => i !== index);
            setCurrentRanges(updatedRanges.length ? updatedRanges : [{ ...emptyRange }]);
        }
    };

    const handleDragStart = (index) => {
        setDraggedItemIndex(index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
    };

    const handleDrop = (index) => {
        if (draggedItemIndex === null) return;

        if (isBloodPressure) {
            const updatedRanges = [...bpRanges[activeBpTab]];
            const draggedItem = updatedRanges[draggedItemIndex];

            // Remove the dragged item
            updatedRanges.splice(draggedItemIndex, 1);
            // Insert it at the new position
            updatedRanges.splice(index, 0, draggedItem);

            setBpRanges({
                ...bpRanges,
                [activeBpTab]: updatedRanges
            });
        } else {
            const updatedRanges = [...currentRanges];
            const draggedItem = updatedRanges[draggedItemIndex];

            // Remove the dragged item
            updatedRanges.splice(draggedItemIndex, 1);
            // Insert it at the new position
            updatedRanges.splice(index, 0, draggedItem);

            setCurrentRanges(updatedRanges);
        }

        setDraggedItemIndex(null);
    };

    const validateRanges = (ranges) => {
        if (!ranges || ranges.length === 0) return 'No ranges defined';

        // Sort ranges by min value
        const sortedRanges = [...ranges].sort((a, b) => {
            const minA = a.min === '' ? -Infinity : parseFloat(a.min);
            const minB = b.min === '' ? -Infinity : parseFloat(b.min);
            return minA - minB;
        });

        let runtill = sortedRanges.length - 1;
        if(sortedRanges[sortedRanges.length -1]?.max !== ""){ 
            runtill = sortedRanges.length;
        }

        for (let i = 0; i < sortedRanges.length; i++) {
            if(parseFloat(sortedRanges[i].min) === parseFloat(sortedRanges[i].max)){ 
                return `Same value for MIN and MAX`;
            }
            else if(parseFloat(sortedRanges[i].min) > parseFloat(sortedRanges[i].max)){
                return `MIN ${sortedRanges[i].min} Ranage can't be greater than MAX ${sortedRanges[i].max}`;
            }
        }

        // Check for gaps or overlaps
        for (let i = 0; i < sortedRanges.length - 1; i++) {
            const currentMax = sortedRanges[i].max === '' ? Infinity : parseFloat(sortedRanges[i].max);
            const nextMin = sortedRanges[i + 1].min === '' ? -Infinity : parseFloat(sortedRanges[i + 1].min);
             
            // Next range should start with value greater than current range's max
            // For example: if current range is 0-20, next range should start from 21
            // If current range is 0-19.4, next range should start from 19.5
            
            // Check if there's a gap or if ranges are consecutive
            if (nextMin <= currentMax) {
                return `Invalid range boundary: ${currentMax} and ${nextMin}. Next range should start with value greater than previous range's maximum.`;
            }

            if(nextMin - currentMax > 1){
                return `Invalid range boundary: ${currentMax} and ${nextMin}. Few values are missing in Range.`;
            } 
        }   

        // Check for empty observations
        const emptyObservation = sortedRanges.find(range => !range.observation.trim());
        if (emptyObservation) {
            return 'All ranges must have an observation';
        }

        // Check for metric limits if applicable
        if (selectedMetric && metricLimits[selectedMetric]) {
            const { min: minLimit, max: maxLimit } = metricLimits[selectedMetric];

            // Find the lowest min and highest max across all ranges
            const lowestMin = Math.min(...sortedRanges.map(r => r.min === '' ? -Infinity : parseFloat(r.min)));
            const highestMax = Math.max(...sortedRanges.map(r => r.max === '' ? Infinity : parseFloat(r.max)));

            // Check if the ranges cover the required range
            if (lowestMin > minLimit) {
                return `Ranges must start at or below the minimum value (${minLimit})`;
            }

            if (highestMax < maxLimit) {
                return `Ranges must end at or above the maximum value (${maxLimit})`;
            }
        }

        return '';
    };

    const handleSaveRanges = async () => {
        let validationError = '';
        let rangesToSave = {};

        if (isBloodPressure) {
            // Validate both systolic and diastolic ranges
            validationError = validateRanges(bpRanges.systolic);
            if (!validationError) {
                validationError = validateRanges(bpRanges.diastolic);
            }

            if (validationError) {
                setError(validationError);
                return;
            }

            // Sort both systolic and diastolic ranges before saving
            const sortedBpRanges = {
                systolic: [...bpRanges.systolic].sort((a, b) => {
                    const minA = a.min === '' ? -Infinity : parseFloat(a.min);
                    const minB = b.min === '' ? -Infinity : parseFloat(b.min);
                    return minA - minB;
                }),
                diastolic: [...bpRanges.diastolic].sort((a, b) => {
                    const minA = a.min === '' ? -Infinity : parseFloat(a.min);
                    const minB = b.min === '' ? -Infinity : parseFloat(b.min);
                    return minA - minB;
                })
            };

            // Prepare data for saving - remove the label property
            const preparedBpRanges = {
                systolic: sortedBpRanges.systolic.map(({ alertPriorityLabel, ...rest }) => rest),
                diastolic: sortedBpRanges.diastolic.map(({ alertPriorityLabel, ...rest }) => rest)
            };

            rangesToSave = preparedBpRanges;
            // Save BP ranges
            setMetricRanges(prevRanges => ({
                ...prevRanges,
                [selectedMetric]: sortedBpRanges
            }));
        } else {
            // Validate regular ranges
            validationError = validateRanges(currentRanges);

            if (validationError) {
                setError(validationError);
                return;
            }

            // Sort ranges by min value before saving
            const sortedRanges = [...currentRanges].sort((a, b) => {
                const minA = a.min === '' ? -Infinity : parseFloat(a.min);
                const minB = b.min === '' ? -Infinity : parseFloat(b.min);
                return minA - minB;
            });

            // Prepare data for saving - remove the label property
            const preparedRanges = sortedRanges.map(({ alertPriorityLabel, ...rest }) => rest);

            rangesToSave = preparedRanges;

            // Save regular ranges
            setMetricRanges(prevRanges => ({
                ...prevRanges,
                [selectedMetric]: sortedRanges
            }));
        }

        try {
            const response = await save_metrics_ranges({ range: JSON.stringify(rangesToSave), metricsId: selectedMetric });
            if (response.data.code === 200) {
                // You might want to add success feedback here
                setEditMode(false);
                // fetch ranges for the selected metric
                fetchMetricRanges(selectedMetric);

            }
        } catch (error) {
            console.log(error);
        }

        // Clear error if validation passes
        setError('');
    };

    // Toggle edit mode
    const toggleEditMode = () => {
        setEditMode(!editMode);
    };

    // Render the current ranges based on whether it's BP or regular metric
    const renderRangesTable = () => {
        if (!selectedMetric) return null;

        const ranges = isBloodPressure ? bpRanges[activeBpTab] : currentRanges;

        if (editMode) {
            // Render editable table when in edit mode
            return (
                <div className="table-responsive BackendDrivenDataTable">
                    <table className="table table-striped mb-0">
                        <thead>
                            <tr className='tw-bg-[#D4E6EF]'>
                                <th><i className="bi bi-list tw-opacity-50 tw-pl-3"></i></th>
                                <th>Min Value</th>
                                <th>Max Value</th>
                                <th>Observation</th>
                                <th>Alert Priority</th>
                                <th>Color</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ranges.map((range, index) => (
                                <tr
                                    key={index}
                                    draggable={true}
                                    onDragStart={() => handleDragStart(index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDrop={() => handleDrop(index)}
                                    style={{
                                        cursor: 'move',
                                        backgroundColor: draggedItemIndex === index ? '#f8f9fa' : 'transparent'
                                    }}
                                >
                                    <td className="text-center">
                                        <i className="bi bi-list tw-opacity-50"></i>
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Leave empty for no lower bound"
                                            value={range.min}
                                            onChange={(e) => handleRangeChange(index, 'min', e.target.value)}
                                            pattern="-?\d*\.?\d*"
                                            title="Please enter a valid number"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Leave empty for no upper bound"
                                            value={range.max}
                                            onChange={(e) => handleRangeChange(index, 'max', e.target.value)}
                                            pattern="-?\d*\.?\d*"
                                            title="Please enter a valid number"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. Healthy, Overweight"
                                            value={range.observation}
                                            onChange={(e) => handleRangeChange(index, 'observation', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <select
                                            className="form-select"
                                            value={range.alertPriorityLabel || getAlertPriorityLabel(range.alertPriority)}
                                            onChange={(e) => handleRangeChange(index, 'alertPriorityLabel', e.target.value)}
                                        >
                                            {Object.keys(RPM_ALERT_PRIORITY).map(key => (
                                                <option key={key} value={key}>
                                                    {key}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="color"
                                            className="form-control form-control-color"
                                            value={range.color}
                                            onChange={(e) => handleRangeChange(index, 'color', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <button
                                            className="tw-border-none tw-bg-transparent tw-text-red-500 hover:tw-text-red-700"
                                            onClick={() => handleRemoveRange(index)}
                                            disabled={ranges.length === 1}
                                        >
                                            <i className="bi bi-trash3"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        } else {
            // Render read-only table when not in edit mode
            return (
                <div className="table-responsive BackendDrivenDataTable">
                    <table className="table table-striped mb-0">
                        <thead>
                            <tr className='tw-bg-[#D4E6EF]'>
                                <th>Min Value</th>
                                <th>Max Value</th>
                                <th>Observation</th>
                                <th>Alert Priority</th>
                                <th>Color</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ranges.map((range, index) => (
                                <tr key={index}>
                                    <td>{range.min === '' ? 'Min' : range.min}</td>
                                    <td>{range.max === '' ? 'Max' : range.max}</td>
                                    <td>{range.observation}</td>
                                    <td>{range.alertPriorityLabel || getAlertPriorityLabel(range.alertPriority)}</td>
                                    <td>
                                        <div
                                            style={{
                                                backgroundColor: range.color,
                                                width: '30px',
                                                height: '30px',
                                                borderRadius: '4px'
                                            }}
                                        ></div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }
    };

    // Render BP tabs if needed
    const renderBloodPressureTabs = () => {
        if (!isBloodPressure) return null;

        return (
            <div className="mb-4">
                <ul className="nav nav-tabs">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeBpTab === 'systolic' ? 'active' : ''}`}
                            onClick={() => setActiveBpTab('systolic')}
                        >
                            Systolic (mmHg)
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeBpTab === 'diastolic' ? 'active' : ''}`}
                            onClick={() => setActiveBpTab('diastolic')}
                        >
                            Diastolic (mmHg)
                        </button>
                    </li>
                </ul>
            </div>
        );
    };

    // Helper to display ranges in the configured metrics table
    const displayRanges = (metricId, ranges) => {
        if (metricId === '6') {
            // Blood pressure has systolic and diastolic
            return (
                <>
                    <strong>Systolic:</strong>
                    <ul className="list-unstyled mb-2">
                        {ranges.systolic.map((range, idx) => (
                            <li key={`sys-${idx}`} style={{ color: range.color }}>
                                {range.min === '' ? '(Min' : `(${range.min}`} to {range.max === '' ? 'Max)' : `${range.max})`}: {range.observation}
                            </li>
                        ))}
                    </ul>
                    <strong>Diastolic:</strong>
                    <ul className="list-unstyled">
                        {ranges.diastolic.map((range, idx) => (
                            <li key={`dia-${idx}`} style={{ color: range.color }}>
                                {range.min === '' ? '(-Min' : `(${range.min}`} to {range.max === '' ? 'Max)' : `${range.max})`}: {range.observation}
                            </li>
                        ))}
                    </ul>
                </>
            );
        } else {
            // Regular metrics
            return (
                <ul className="list-unstyled">
                    {ranges.map((range, idx) => (
                        <li key={idx} style={{ color: range.color }}>
                            {range.min === '' ? '(-Min' : `(${range.min}`} to {range.max === '' ? 'Max)' : `${range.max})`}: {range.observation}
                        </li>
                    ))}
                </ul>
            );
        }
    };

    return (
        <div>
            {/* <div className="row mb-4">
                    <div className="col-md-12">
                        <h4>Health Metrics Configuration</h4>
                        <p className="text-muted">Configure ranges and conditions for different health metrics</p>
                    </div>
                </div> */}

            <div className="mb-4">
                {/* <div className="col-md-12">
                        <div className="card">
                            <div className="card-body"> */}
                <h5 className="mb-4">Configure Metric Ranges</h5>

                <div className="row mb-4">
                    <div className="col-md-6">
                        <label className="form-label">Select Metric</label>
                        <select
                            className="form-select"
                            value={selectedMetric}
                            onChange={(e) => setSelectedMetric(e.target.value)}
                        >
                            <option value="">Choose...</option>
                            {metrics.map(metric => (
                                <option key={metric.metricsId} value={metric.metricsId}>
                                    {metric.name} ({metric.unit})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {selectedMetric && (
                    <>
                        {error && (
                            <div className="alert alert-danger">{error}</div>
                        )}

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">
                                {metrics.find(m => m.metricsId === parseInt(selectedMetric))?.name} Ranges
                            </h6>
                        </div>

                        {renderBloodPressureTabs()}
                        {renderRangesTable()}


                        {
                            !editMode &&
                            (
                                <div className='tw-w-full tw-flex tw-justify-end tw-mt-4'>
                                    <button
                                        className={`btn btn-primary add-new-program-btn`}
                                        onClick={toggleEditMode}
                                    >
                                        {editMode ? 'Cancel Editing' : 'Edit Ranges'}
                                    </button>
                                </div>
                            )
                        }

                        {editMode && (
                            <div className="d-flex gap-4 tw-mt-4 tw-w-full tw-justify-end">
                                <button
                                    className="tw-border-none tw-bg-transparent tw-text-[#03335B] hover:tw-text-[#1279BE] tw-font-bold tw-underline tw-w-[80%]"
                                    onClick={handleAddRange}
                                >
                                    + Add Another Range
                                </button>


                                <button
                                    className="btn btn-primary add-new-program-btn"
                                    onClick={handleSaveRanges}
                                >
                                    Save Ranges
                                </button>
                            </div>
                        )}
                    </>
                )}
                {/* </div>
                        </div>
                    </div> */}
            </div>
        </div>
    )
}

export default HealthMetricsSettings
